import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { useFrame, useThree, ThreeEvent } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import {
  useGetDevicesByIdQuery,
  usePatchDevicesByIdMutation,
  useGetDevicesByDeviceIdSensorsQuery,
  useGetAnalyticsTimeSeriesQuery,
} from '@/services';
import { TemperatureHaze } from './temperature-haze';
import { HumidityFog } from './humidity-fog';
import { PressureField } from './pressure-field';
import { LightBeam } from './light-beam';
import { MotionWave } from './motion-wave';
import { DeviceModel } from '../models/device-model';

interface DeviceCubeProps {
  position: [number, number, number];
  deviceId: string;
  isSelected: boolean;
  onDeviceClick: (deviceId: string) => void;
  onPositionChange?: (deviceId: string, newPosition: [number, number, number]) => void;
  isEditorMode?: boolean;
  onEditorExit?: () => void;
  sensorTypeFilters?: string[];
  showHeatmap?: boolean;
}

// Generate a random color based on device ID for consistency
const getDeviceColor = (deviceId: string): THREE.Color => {
  const hash = deviceId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const hue = (hash % 360) / 360;
  return new THREE.Color().setHSL(hue, 0.7, 0.5);
};

// Generate consistent colors for sensor types
const getSensorColor = (sensorType: string): string => {
  const colorMap: { [key: string]: string } = {
    temperature: '#ff6b6b',
    humidity: '#4dabf7',
    pressure: '#69db7c',
    light: '#ffd43b',
    motion: '#cc5de8',
    voltage: '#ff9b',
    current: '#20c997',
    power: '#fa5252',
    default: '#adb5bd',
  };

  const type = sensorType.toLowerCase();
  return colorMap[type] || colorMap.default;
};

export function DeviceCube({
  position,
  deviceId,
  isSelected,
  onDeviceClick,
  onPositionChange,
  isEditorMode = false,
  onEditorExit,
  sensorTypeFilters,
  showHeatmap = false,
}: DeviceCubeProps) {
  const groupRef = useRef<THREE.Group>(null);
  const { data: deviceData } = useGetDevicesByIdQuery({ id: deviceId });
  const { data: positionData } = useGetDevicesByDeviceIdSensorsQuery({ deviceId });
  const [updateDevicePosition] = usePatchDevicesByIdMutation();
  const deviceName = deviceData?.data?.name || deviceId;

  useEffect(() => {
      console.log("showHeatmap", showHeatmap)
  }, [showHeatmap])

  // Fetch time series data for sensors
  const { data: timeSeriesData } = useGetAnalyticsTimeSeriesQuery(
    {
      deviceIds: deviceId,
      interval: 'hour',
      limit: '1',
    },
    {
      pollingInterval: 5000, // Poll every 5 seconds
    }
  );

  // Process sensor data from time series
  const sensors = timeSeriesData?.data?.map(series => {
    const latestReading = series.series[0];
    return {
      id: series.sensor_id,
      name: series.sensor_name,
      type: series.sensor_type,
      value: latestReading?.value || 0,
      unit: series.unit || '',
      isOnline: true,
      position: [0, 0, 0] as [number, number, number],
      deviceName: series.device_name || 'Unknown Device',
      deviceId: series.device_id,
      timestamp: latestReading?.timestamp || new Date().toISOString(),
    };
  }) || [];

  // Filter sensors based on sensorTypeFilters if provided
  const filteredSensors = sensorTypeFilters
    ? sensorTypeFilters.length === 0
      ? sensors
      : sensors.filter((sensor) => sensorTypeFilters.includes(sensor.type?.toLowerCase()))
    : sensors;

  // Use position from API if it exists and is valid, otherwise use the provided position
  const initialPosition: [number, number, number] =
    positionData?.data?.position &&
    typeof positionData.data.position === 'object' &&
    'x' in positionData.data.position &&
    'y' in positionData.data.position &&
    'z' in positionData.data.position &&
    typeof positionData.data.position.x === 'number' &&
    typeof positionData.data.position.y === 'number' &&
    typeof positionData.data.position.z === 'number'
      ? [positionData.data.position.x, positionData.data.position.y, positionData.data.position.z]
      : position;

  const [localPosition, setLocalPosition] = useState<[number, number, number]>(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const { camera, raycaster, mouse } = useThree();
  const [dragPlane] = useState(() => new THREE.Plane());
  const [dragOffset] = useState(() => new THREE.Vector3());
  const [hasPositionChanged, setHasPositionChanged] = useState(false);

  // Update local position when API position changes
  useEffect(() => {
    if (
      positionData?.data?.position &&
      typeof positionData.data.position === 'object' &&
      'x' in positionData.data.position &&
      'y' in positionData.data.position &&
      'z' in positionData.data.position &&
      typeof positionData.data.position.x === 'number' &&
      typeof positionData.data.position.y === 'number' &&
      typeof positionData.data.position.z === 'number'
    ) {
      const newPosition: [number, number, number] = [
        positionData.data.position.x,
        positionData.data.position.y,
        positionData.data.position.z,
      ];
      setLocalPosition(newPosition);
      if (onPositionChange) {
        onPositionChange(deviceId, newPosition);
      }
    }
  }, [positionData, deviceId, onPositionChange]);

  // Reset position changed flag when editor mode changes
  useEffect(() => {
    if (!isEditorMode && hasPositionChanged) {
      // Update device position in the backend when editor mode is exited
      updateDevicePosition({
        id: deviceId,
        body: {
          position: {
            x: localPosition[0],
            y: localPosition[1],
            z: localPosition[2],
          },
        },
      });
      setHasPositionChanged(false);
      if (onEditorExit) {
        onEditorExit();
      }
    }
  }, [isEditorMode, hasPositionChanged, deviceId, localPosition, updateDevicePosition, onEditorExit]);

  // Add hover effect
  useEffect(() => {
    if (!groupRef.current) return;

    const group = groupRef.current;
    const mainBody = group.children[0] as THREE.Mesh;
    const material = mainBody.material as THREE.MeshStandardMaterial;
    const originalColor = getDeviceColor(deviceId).clone();

    const onPointerOver = () => {
      if (isEditorMode) {
        document.body.style.cursor = 'move';
      } else {
        document.body.style.cursor = 'pointer';
      }
      if (!isSelected) {
        const hsl = originalColor.getHSL({ h: 0, s: 0, l: 0 });
        material.color.setHSL(hsl.h, 0.8, 0.6);
        material.emissive.set(getDeviceColor(deviceId));
        material.emissiveIntensity = 0.2;
      }
    };

    const onPointerOut = () => {
      document.body.style.cursor = 'auto';
      if (!isSelected) {
        material.color.copy(originalColor);
        material.emissive.set(0x000000);
        material.emissiveIntensity = 0;
      }
    };

    group.addEventListener('pointerover' as keyof THREE.Object3DEventMap, onPointerOver);
    group.addEventListener('pointerout' as keyof THREE.Object3DEventMap, onPointerOut);

    return () => {
      group.removeEventListener('pointerover' as keyof THREE.Object3DEventMap, onPointerOver);
      group.removeEventListener('pointerout' as keyof THREE.Object3DEventMap, onPointerOut);
    };
  }, [deviceId, isSelected, isEditorMode]);

  // Handle dragging
  useFrame(() => {
    if (isDragging && groupRef.current && isEditorMode) {
      raycaster.setFromCamera(mouse, camera);

      // Calculate the new position based on the ray intersection with the drag plane
      const intersectionPoint = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(dragPlane, intersectionPoint)) {
        // Apply the offset to maintain the initial click position relative to the object
        intersectionPoint.add(dragOffset);

        // Lock Y position to prevent vertical movement
        const newPosition: [number, number, number] = [
          intersectionPoint.x,
          localPosition[1], // Keep the same Y position
          intersectionPoint.z
        ];

        setLocalPosition(newPosition);
        setHasPositionChanged(true);
        if (onPositionChange) {
          onPositionChange(deviceId, newPosition);
        }
      }
    }
  });

  // Remove floating animation in editor mode
  useFrame((state) => {
    if (groupRef.current && !isDragging && !isSelected && !isEditorMode) {
      groupRef.current.position.y = localPosition[1] + Math.sin(state.clock.elapsedTime * 2) * 0.1;
    }
  });

  const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
    if (!isEditorMode) return;

    e.nativeEvent.stopPropagation();

    // Calculate the drag plane based on the camera's view direction
    const cameraDirection = new THREE.Vector3();
    camera.getWorldDirection(cameraDirection);
    dragPlane.setFromNormalAndCoplanarPoint(cameraDirection, new THREE.Vector3(...localPosition));

    // Calculate the offset from the click point to the object's center
    const intersectionPoint = new THREE.Vector3();
    raycaster.setFromCamera(mouse, camera);
    if (raycaster.ray.intersectPlane(dragPlane, intersectionPoint)) {
      dragOffset.copy(new THREE.Vector3(...localPosition)).sub(intersectionPoint);
    }

    setIsDragging(true);
    document.body.style.cursor = 'grabbing';
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    document.body.style.cursor = isEditorMode ? 'move' : 'auto';
  };

  const handlePointerOver = () => {
    if (isEditorMode) {
      document.body.style.cursor = 'move';
    } else {
      document.body.style.cursor = 'pointer';
    }
    if (!isSelected && groupRef.current) {
      const mainBody = groupRef.current.children[0] as THREE.Mesh;
      const material = mainBody.material as THREE.MeshStandardMaterial;
      const originalColor = getDeviceColor(deviceId);
      const hsl = originalColor.getHSL({ h: 0, s: 0, l: 0 });
      material.color.setHSL(hsl.h, 0.8, 0.6);
      material.emissive.set(getDeviceColor(deviceId));
      material.emissiveIntensity = 0.2;
    }
  };

  const handlePointerOut = () => {
    document.body.style.cursor = 'auto';
    if (!isSelected && groupRef.current) {
      const mainBody = groupRef.current.children[0] as THREE.Mesh;
      const material = mainBody.material as THREE.MeshStandardMaterial;
      material.color.copy(getDeviceColor(deviceId));
      material.emissive.set(0x000000);
      material.emissiveIntensity = 0;
    }
  };

  useEffect(() => {
    const handleGlobalPointerUp = () => {
      if (isDragging) {
        setIsDragging(false);
        document.body.style.cursor = isEditorMode ? 'move' : 'auto';
      }
    };

    window.addEventListener('pointerup', handleGlobalPointerUp);
    return () => window.removeEventListener('pointerup', handleGlobalPointerUp);
  }, [isDragging, isEditorMode]);

  // Get sensor values
  const temperatureSensor = filteredSensors.find(sensor => sensor.type?.toLowerCase() === 'temperature');
  const humiditySensor = filteredSensors.find(sensor => sensor.type?.toLowerCase() === 'humidity');
  const pressureSensor = filteredSensors.find(sensor => sensor.type?.toLowerCase() === 'pressure');
  const lightSensor = filteredSensors.find(sensor => sensor.type?.toLowerCase() === 'light');
  const motionSensor = filteredSensors.find(sensor => sensor.type?.toLowerCase() === 'motion');

  return (
    <group position={localPosition}>
      <DeviceModel
        color={getDeviceColor(deviceId)}
        isSelected={isSelected}
        scale={isSelected ? 1.2 : 1}
        onClick={(e: ThreeEvent<MouseEvent>) => {
          e.nativeEvent.stopPropagation();
          onDeviceClick(deviceId);
        }}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      />
      {temperatureSensor && showHeatmap && (
        <TemperatureHaze
          temperature={temperatureSensor.value}
        />
      )}
      {humiditySensor && showHeatmap && (
        <HumidityFog
          humidity={humiditySensor.value}
        />
      )}
      {pressureSensor && showHeatmap && (
        <PressureField
          pressure={pressureSensor.value}
        />
      )}
      {lightSensor && showHeatmap && (
        <LightBeam
          intensity={lightSensor.value}
        />
      )}
      {motionSensor && showHeatmap && (
        <MotionWave
          isActive={motionSensor.value > 0}
        />
      )}
      <Html
        position={[0, isEditorMode ? 1.5 : 0, 0]}
        center
        style={{
          background: 'rgba(0, 0, 0, 0.8)',
          padding: '4px 8px',
          borderRadius: '4px',
          color: 'white',
          fontSize: '12px',
          fontFamily: 'sans-serif',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '2px',
          transform: isEditorMode ? 'none' : 'translateY(-50%)',
          zIndex: isSelected ? 1 : 0,
          boxShadow: isSelected ? '0 0 10px rgba(255, 255, 255, 0.3)' : 'none',
        }}
      >
        <div
          style={{
            textShadow: '0 0 10px rgba(255, 255, 255, 0.5)',
            fontWeight: 'bold',
            padding: '2px 4px',
            fontSize: '14px',
            color: isSelected ? getDeviceColor(deviceId).getStyle() : 'white',
          }}
        >
          {deviceName}
        </div>
        {!isEditorMode && filteredSensors.length > 0 && (
          <div
            style={{
              fontSize: '12px',
              opacity: 0.9,
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            {filteredSensors.map((sensor, index) => {
              const sensorType = sensor.type || 'default';
              const sensorColor = getSensorColor(sensorType);
              return (
                <div
                  key={sensor.id || index}
                  style={{
                    color: sensorColor,
                    textShadow: `0 0 8px ${sensorColor}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <span style={{ opacity: 0.8 }}>{sensor.name || sensor.type}:</span>
                  <span style={{ fontWeight: 'bold' }}>{sensor.value.toFixed(2)}</span>
                  <span style={{ fontWeight: 'bold' }}>{sensor.unit}</span>
                </div>
              );
            })}
          </div>
        )}
      </Html>
    </group>
  );
}

interface DeviceCubesProps {
  devices: Array<{ id: string; position: [number, number, number] }>;
  selectedDevices: string[];
  onDeviceClick: (deviceId: string) => void;
  onPositionChange?: (deviceId: string, newPosition: [number, number, number]) => void;
  isEditorMode?: boolean;
  onEditorExit?: () => void;
  sensorTypeFilters?: string[];
  showHeatmap?: boolean;
}

export function DeviceCubes({
  devices,
  selectedDevices,
  onDeviceClick,
  onPositionChange,
  isEditorMode = false,
  onEditorExit,
  sensorTypeFilters,
  showHeatmap = false,
}: DeviceCubesProps) {
  // Group devices by their y-position to avoid label overlap
  const deviceGroups = devices.reduce((groups, device) => {
    const yPos = Math.round(device.position[1] * 10) / 10; // Round to 1 decimal place
    if (!groups[yPos]) {
      groups[yPos] = [];
    }
    groups[yPos].push(device);
    return groups;
  }, {} as { [key: number]: typeof devices });

  return (
    <group>
      {Object.entries(deviceGroups).map(([yPos, groupDevices]) => (
        <group key={yPos}>
          {groupDevices.map((device, index) => (
            <DeviceCube
              key={device.id}
              position={[
                device.position[0] + (index * 2), // Offset devices horizontally
                device.position[1],
                device.position[2]
              ]}
              deviceId={device.id}
              isSelected={selectedDevices.includes(device.id)}
              onDeviceClick={onDeviceClick}
              onPositionChange={onPositionChange}
              isEditorMode={isEditorMode}
              onEditorExit={onEditorExit}
              sensorTypeFilters={sensorTypeFilters}
              showHeatmap={showHeatmap}
            />
          ))}
        </group>
      ))}
    </group>
  );
}
